/**
 * metaCompare — "compare your Pokémon to the meta" (2026-09-10 owner ask).
 *
 * Under every ranked row on the PvP and Raid lists sits a small box that
 * says, in one glance, where the trainer stands against that row:
 *
 *   MISSING     no copy in the box for that row (red)
 *   WRONG MOVES the best copy is there, but its scanned moves are not the
 *               ranked moveset, or its moves were never scanned (yellow)
 *   RIGHT MOVES the best copy runs the ranked moveset (green)
 *
 * The copy is chosen by lib/yoursMetaBest (destiny-aware for PvP, exact form
 * for raids), so the box agrees with the Yours tab. Move names compare
 * loosely, the way myPokemonRecommendations already does: case, spaces and
 * punctuation do not count, so "Wild Charge" and "wildcharge" are one move.
 * A copy with no scanned moves cannot be called right, and cannot be called
 * missing either: yellow, with the reason spelled out in the box.
 */
import type { BattleMetaEntry } from "./generated/battleMeta.generated";
import type { RaidRanked } from "./raidMeta";
import type { MyPokemonRecord } from "./myPokemonStore";
import {
  bestPvpCopyFor,
  bestRaidCopyFor,
  type YoursLeagueKey,
} from "./yoursMetaBest";

export type MetaCompareState = "missing" | "wrong-moves" | "right-moves";

export interface MetaCompare {
  state: MetaCompareState;
  /** The best copy, or null when missing. */
  record: MyPokemonRecord | null;
  /** The moves the row wants, display names. */
  wanted: string[];
  /** The copy's scanned moves, display names (empty when never scanned). */
  have: string[];
  /** Set when the copy claims the row as an evolution it has not made yet. */
  asForm: { speciesId: number; speciesName: string } | null;
  /**
   * How good the copy is FOR THIS ROW: its percent-of-best in the league
   * (destiny-aware) on the PvP list, its IV percent on the raid list. Null
   * when missing or unscanned. `percentLabel` names which it is.
   */
  percent: number | null;
  percentLabel: string;
}

function normalizeMove(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]/g, "");
}

/** True when every wanted move is among the record's scanned moves. */
export function runsMoveset(record: MyPokemonRecord, wanted: readonly string[]): boolean {
  if (wanted.length === 0 || record.moves.length === 0) return false;
  const have = new Set(record.moves.map((m) => normalizeMove(m.name)));
  return wanted.every((m) => have.has(normalizeMove(m)));
}

function judge(
  record: MyPokemonRecord | null,
  wanted: string[],
  asForm: MetaCompare["asForm"],
  percent: number | null,
  percentLabel: string,
): MetaCompare {
  if (!record) {
    return { state: "missing", record: null, wanted, have: [], asForm: null, percent: null, percentLabel };
  }
  return {
    state: runsMoveset(record, wanted) ? "right-moves" : "wrong-moves",
    record,
    wanted,
    have: record.moves.map((m) => m.name),
    asForm,
    percent,
    percentLabel,
  };
}

const LEAGUE_SHORT: Record<YoursLeagueKey, string> = { great: "Great", ultra: "Ultra", master: "Master" };

/** The trainer's standing against one PvP ranked row. */
export function comparePvpEntry(
  records: readonly MyPokemonRecord[],
  league: YoursLeagueKey,
  entry: BattleMetaEntry,
): MetaCompare {
  const pick = bestPvpCopyFor(records, league, entry);
  return judge(pick?.record ?? null, entry.moves, pick?.asForm ?? null, pick?.percent ?? null, LEAGUE_SHORT[league]);
}

/** The trainer's standing against one raid ranked row. */
export function compareRaidRow(
  records: readonly MyPokemonRecord[],
  row: RaidRanked,
): MetaCompare {
  const pick = bestRaidCopyFor(records, row);
  const wanted = [row.fast?.name, row.charged?.name].filter((m): m is string => !!m);
  return judge(pick?.record ?? null, wanted, null, pick?.ivPercent ?? null, "IV");
}

/** A league key the PvP compare can use, or null for a league it does not know. */
export function compareLeagueKey(key: string): YoursLeagueKey | null {
  return key === "great" || key === "ultra" || key === "master" ? key : null;
}
